import moment from 'moment';
import { nullSafeGet, nullSafeGetOrElse, nullSafeGetWithZero } from './DataAccessUtils';
import {
	formatLeakRatePercent,
	formatPounds,
	getApplianceCategoryLabel,
	getChargeReasonLabel,
	getRecoveryReasonLabel,
	getRefrigerantComplianceStatusLabel,
	getRefrigerantEventTypeLabel,
	getRefrigerantProgramme,
	getRefrigerantProgrammeStandardsLine,
	getVerificationStageLabel,
} from './RefrigerantUtils';

/**
 * A leading character that makes Excel (and Sheets, and Numbers) treat a cell as a FORMULA rather
 * than as text. Quoting does not disarm them — the sheet strips the quotes on import and then
 * evaluates what is inside — so they have to be neutralised in the value itself.
 */
const FORMULA_LEAD_CHARACTERS = ['=', '+', '-', '@'];

/**
 * RFC 4180 quoting. Every field is quoted rather than only the ones that need it: a notes field is
 * free text typed in a mechanical room, and deciding per-value which characters are "safe" is how a
 * stray comma silently shifts every column after it on a compliance export.
 *
 * A formula-leading value additionally gets a TAB in front of it. This file writes a BOM for Excel
 * (see below), which means Excel is the intended reader, and Excel evaluates `=...` in a CSV cell
 * even when the field is quoted — so a technician's note or an asset name beginning with `=`, `+`,
 * `-` or `@` becomes executable content in a document that gets emailed to auditors. The tab makes
 * the cell unambiguously text, renders as nothing, and cannot shift a column the way an injected
 * comma or quote would. An apostrophe would work too, but it survives into the raw file and into
 * anything that is not Excel; the tab does not.
 */
const toCsvField = (value) => {
	if (value === null || value === undefined) return '""';
	const text = String(value);
	const guarded = FORMULA_LEAD_CHARACTERS.indexOf(text.charAt(0)) === -1 ? text : `\t${text}`;
	return `"${guarded.replace(/"/g, '""')}"`;
};

const toCsvRow = (fields) => fields.map(toCsvField).join(',');

const formatCertification = (event) =>
	[nullSafeGet('certificationType', event), nullSafeGet('certificationNumber', event)]
		.filter(Boolean)
		.join(' ');

/**
 * Resolved exactly as the on-screen Tech column resolves it — see `getRefrigerantEventLedgerColumns`,
 * which this file deliberately does not import — so the two cannot disagree about who a row credits.
 *
 * The event's OWN `technicianName` snapshot comes first: it is the person the row's certification
 * attests to, written by the server either from the picked technician's contact or from the free text
 * captured beside the 608 card of a certified third party who has no login here. On that second path
 * the contacts below describe only whoever FILED the row, so crediting them would print the wrong name
 * next to a real certificate. Never read off the live contact, for the same reason the certification
 * is not: a card renewed next year must not rewrite what this record attests to.
 */
const technicianName = (event) => {
	const contact = nullSafeGet('technicianContact', event) || nullSafeGet('createdByContact', event);
	const contactName = `${nullSafeGetOrElse('nameGiven', contact, '')} ${nullSafeGetOrElse(
		'nameFamily',
		contact,
		''
	)}`.trim();
	return (
		nullSafeGet('technicianName', event) ||
		contactName ||
		nullSafeGet('technicianEmail', event) ||
		nullSafeGet('createdBy', event) ||
		''
	);
};

/**
 * The servicing record as a CSV — the header block as `key,value` pairs, then the service events.
 *
 * Built client-side from what the record already rendered, deliberately: the export and the document
 * on screen must agree field for field, and a server-side regeneration would recompute the summary at
 * a different instant and could disagree with the page the user was looking at when they clicked.
 *
 * Dates are written in UTC, matching how the record renders them — see `RefrigerantEventDate`. An
 * export that shifts an `occurredAt` into the reader's zone can move an event to the previous day.
 *
 * The threshold and band are PASSED IN rather than read off the summary: they depend on which
 * programme tab is open, and re-deriving them here would let the file disagree with the sheet the
 * user was looking at when they clicked.
 */
/**
 * The record's CSV text. Split out from the download so the bytes this produces can be asserted on
 * directly — the formula-neutralising in `toCsvField` is a security property of the OUTPUT, and a
 * property that is only reachable through a Blob and an anchor click is a property nothing tests.
 */
export const buildServicingRecordCsv = ({
	asset,
	assetTag,
	locationLabel,
	summary,
	events,
	companyName,
	programmeKey,
	thresholdPercent,
	band,
}) => {
	const programme = getRefrigerantProgramme(programmeKey);

	const headerRows = [
		['Refrigerant servicing record'],
		[programme.name],
		['Standards', getRefrigerantProgrammeStandardsLine(programmeKey)],
		['Generated', moment().format('MMM DD, YYYY')],
		['Company', companyName || ''],
		['Asset', nullSafeGetOrElse('name', asset, '')],
		['Asset Tag', assetTag === '--' ? '' : assetTag],
		['Location', locationLabel === '--' ? '' : locationLabel],
		[
			'Category',
			nullSafeGet('applianceCategory', summary)
				? getApplianceCategoryLabel(nullSafeGet('applianceCategory', summary))
				: '',
		],
		['Refrigerant', nullSafeGetOrElse('refrigerantTypeCode', summary, '')],
		['Full charge', formatPounds(nullSafeGetWithZero('fullChargeLb', summary))],
		['12-month leak rate', formatLeakRatePercent(nullSafeGetWithZero('leakRatePercent', summary))],
		[
			programme.thresholdLabel,
			thresholdPercent === null || thresholdPercent === undefined ? '' : `${thresholdPercent}%`,
		],
		['Status', getRefrigerantComplianceStatusLabel(band)],
		[],
		['Date', 'Event Details', 'Tech', 'Certification', 'Work Order', 'Notes'],
	];

	const eventRows = (events || []).map((event) => [
		nullSafeGet('occurredAt', event)
			? moment.utc(nullSafeGet('occurredAt', event)).format('D MMMM YYYY')
			: '',
		// This is a SECOND, independent implementation of `RefrigerantEventTypeDisplay`'s suffix logic —
		// the export builds text, not React — and the two must agree field for field, because an
		// auditor reads the CSV against the sheet the user printed. So a field added to one has to be
		// added here in the same change; the charge reason is the field that would otherwise vanish
		// from the evidence while remaining on screen.
		[
			getRefrigerantEventTypeLabel(nullSafeGet('eventType', event)),
			nullSafeGetWithZero('amountLb', event) === null ||
			nullSafeGetWithZero('amountLb', event) === undefined
				? null
				: formatPounds(nullSafeGetWithZero('amountLb', event)),
			nullSafeGet('result', event),
			// The stage is what tells an initial pass from a follow-up pass, and a repair's obligation is
			// discharged only by the follow-up. Without it the file held two indistinguishable passes and
			// an auditor could not tell a discharged obligation from a repeated initial test — while the
			// screen showed the difference. This is the field-for-field agreement the comment above asks
			// for.
			getVerificationStageLabel(nullSafeGet('verificationStage', event)) || null,
			nullSafeGet('leakSource', event) ? `Leak Source: ${nullSafeGet('leakSource', event)}` : null,
			nullSafeGet('chargeReason', event)
				? `Reason for Charge: ${getChargeReasonLabel(nullSafeGet('chargeReason', event))}`
				: null,
			// The recovery half, and the field that carries what a separate `Disposal` event label used to
			// say all by itself. Without it the CSV cannot tell a servicing pump-down from an end-of-life
			// disposal, while the sheet beside it can — exactly the field-for-field disagreement the note
			// above forbids, on the one row an EPA disposal record points at.
			nullSafeGet('recoveryReason', event)
				? `Reason for Recovery: ${getRecoveryReasonLabel(nullSafeGet('recoveryReason', event))}`
				: null,
		]
			.filter(Boolean)
			.join(' · '),
		technicianName(event),
		formatCertification(event),
		nullSafeGet('workOrderId', event) ? `#WO ${nullSafeGet('workOrderId', event)}` : '',
		nullSafeGetOrElse('notes', event, ''),
	]);

	return [...headerRows, ...eventRows].map(toCsvRow).join('\n');
};

export const downloadServicingRecordCsv = (recordParams) => {
	const programme = getRefrigerantProgramme(recordParams.programmeKey);
	const csv = buildServicingRecordCsv(recordParams);

	// A BOM so Excel opens the file as UTF-8. Without it a refrigerant designation or a tech's name
	// carrying a non-ASCII character renders as mojibake in the one tool auditors actually use.
	const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	// The programme is in the FILENAME as well as the header: the same appliance exports three
	// different records, and three files called servicing-record-asset-42.csv in a downloads folder
	// are indistinguishable from each other.
	link.download = `servicing-record-${programme.key}-asset-${nullSafeGetOrElse(
		'id',
		recordParams.asset,
		'unknown'
	)}.csv`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};
